import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useServices } from "@/hooks/useServices";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, DollarSign, ChevronDown, Plus, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import PromoBanner from "@/components/PromoBanner";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import type { ServiceAddon } from "@/hooks/useServiceAddons";

const CATEGORY_OG: Record<string, { title: string; description: string; image: string }> = {
  nails: {
    title: "Luxury Nails — In Paradise Beauty",
    description: "Beauty, pure passion — book your luxury nail experience now.",
    image: "/og-nails.jpg",
  },
  lashes: {
    title: "Lash Artistry — In Paradise Beauty",
    description: "Beauty, pure passion — book your lash experience now.",
    image: "/og-lashes.jpg",
  },
  skincare: {
    title: "Glowing Skincare — In Paradise Beauty",
    description: "Beauty, pure passion — book your skincare experience now.",
    image: "/og-skincare.jpg",
  },
};

const slugify = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0, 0, 0.2, 1] as const },
  }),
};

const Services = () => {
  const { categories: services } = useServices();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = (searchParams.get("category") || "").toLowerCase();
  const og = CATEGORY_OG[categoryParam];

  // Track which service card is expanded: "categoryId__serviceName"
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  // All addons keyed by category_id
  const [addonsByCategory, setAddonsByCategory] = useState<Record<string, ServiceAddon[]>>({});
  const [loadingAddons, setLoadingAddons] = useState<string | null>(null);
  // Selected addon IDs for the currently expanded service
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!categoryParam || services.length === 0) return;
    const match = services.find(
      (c) => slugify(c.title) === categoryParam || slugify(c.title).includes(categoryParam)
    );
    if (!match) return;
    const t = setTimeout(() => {
      document.getElementById(`cat-${slugify(match.title)}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [categoryParam, services]);

  const toggleService = async (categoryId: string, serviceName: string) => {
    const key = `${categoryId}__${serviceName}`;
    if (expandedKey === key) {
      setExpandedKey(null);
      setSelectedAddonIds(new Set());
      return;
    }

    setExpandedKey(key);
    setSelectedAddonIds(new Set());

    // Fetch addons for this category if not already loaded
    if (!addonsByCategory[categoryId]) {
      setLoadingAddons(categoryId);
      const { data } = await supabase
        .from("service_addons")
        .select("*")
        .eq("category_id", categoryId)
        .order("sort_order");
      setAddonsByCategory((prev) => ({
        ...prev,
        [categoryId]: (data || []).map((a: any) => ({
          id: a.id,
          category_id: a.category_id,
          name: a.name,
          duration: a.duration,
          price: a.price,
          sort_order: a.sort_order,
        })),
      }));
      setLoadingAddons(null);
    }
  };

  const toggleAddon = (addonId: string) => {
    setSelectedAddonIds((prev) => {
      const next = new Set(prev);
      if (next.has(addonId)) next.delete(addonId);
      else next.add(addonId);
      return next;
    });
  };

  const handleBook = (categoryTitle: string, serviceName: string, categoryId: string) => {
    const addons = addonsByCategory[categoryId] || [];
    const selectedAddons = addons.filter((a) => selectedAddonIds.has(a.id));
    const addonNames = selectedAddons.map((a) => a.name).join(",");

    const bookUrl = `/book?category=${encodeURIComponent(categoryTitle)}&service=${encodeURIComponent(serviceName)}${addonNames ? `&addons=${encodeURIComponent(addonNames)}` : ""}`;

    if (user) {
      navigate(bookUrl);
    } else {
      navigate(`/auth?returnTo=${encodeURIComponent(bookUrl)}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        path={`/services${categoryParam ? `?category=${categoryParam}` : ""}`}
        title={og?.title ?? "Services & Experiences — In Paradise Beauty"}
        description={og?.description ?? "Explore luxury nails, lashes, pedicures and skincare. Find your next beauty experience at In Paradise Beauty."}
        image={og?.image}
      />
      <Navbar />
      <PromoBanner />

      {/* Header */}
      <section className="pt-32 pb-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">Paradise Beauty</p>
          <h1 className="font-serif text-5xl md:text-6xl font-bold italic mb-4">
            <span className="gold-shimmer">Services &amp; Experiences</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto font-light">
            Browse the full menu of luxury beauty experiences and find the perfect treatment for you.
          </p>
        </div>
      </section>

      {/* Service Categories */}
      <section className="pb-24">
        <div className="container mx-auto px-6 space-y-24">
          {services.map((category) => (
            <motion.div
              key={category.id}
              id={`cat-${slugify(category.title)}`}
              className="scroll-mt-28"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
            >
              <motion.div variants={fadeUp} custom={0} className="flex items-center gap-5 mb-10">
                <span className="text-4xl">{category.icon}</span>
                <div>
                  <h2 className="font-serif text-3xl md:text-4xl font-bold italic">{category.title}</h2>
                  <p className="text-muted-foreground text-sm font-light tracking-wide">{category.tagline}</p>
                </div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.subServices.map((sub, i) => {
                  const key = `${category.id}__${sub.name}`;
                  const isExpanded = expandedKey === key;
                  const categoryAddons = addonsByCategory[category.id] || [];
                  const isLoadingThis = loadingAddons === category.id && isExpanded;

                  return (
                    <div
                      key={sub.name}
                      className="bg-card border border-border/50 hover:border-primary/40 transition-colors hover:shadow-[0_0_20px_hsl(40_80%_42%/0.08)]"
                    >

                      {/* Service card header */}
                      <div
                        className="p-6 cursor-pointer group"
                        onClick={() => toggleService(category.id, sub.name)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-base mb-1 group-hover:text-primary transition-colors">{sub.name}</h3>
                          <ChevronDown
                            size={16}
                            className={`text-muted-foreground mt-1 shrink-0 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                          />
                        </div>
                        <p className="text-muted-foreground text-sm mb-5 leading-relaxed font-light">{sub.description}</p>
                        <div className="flex items-center justify-between text-sm border-t border-border/30 pt-4">
                          <span className="flex items-center gap-1.5 text-muted-foreground text-xs tracking-[0.15em] uppercase">
                            <Clock size={13} /> {sub.duration}
                          </span>
                          <span className="flex items-center gap-1 text-primary font-semibold">
                            <DollarSign size={14} /> {sub.price}
                          </span>
                        </div>
                      </div>

                      {/* Expandable add-ons + book button */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-6 border-t border-border/30">
                              {/* Add-ons */}
                              {isLoadingThis ? (
                                <p className="text-muted-foreground text-xs py-4 text-center">Loading add-ons...</p>
                              ) : categoryAddons.length > 0 ? (
                                <div className="pt-4 space-y-2">
                                  <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Add-Ons</p>
                                  {categoryAddons.map((addon) => {
                                    const isSelected = selectedAddonIds.has(addon.id);
                                    return (
                                      <button
                                        key={addon.id}
                                        onClick={() => toggleAddon(addon.id)}
                                        className={`w-full flex items-center justify-between p-3 text-left transition-all border ${
                                          isSelected
                                            ? "border-primary/50 bg-primary/5"
                                            : "border-border/30 hover:border-primary/20"
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <div
                                            className={`w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${
                                              isSelected
                                                ? "bg-primary border-primary"
                                                : "border-muted-foreground/30"
                                            }`}
                                          >
                                            {isSelected && <Check size={12} className="text-primary-foreground" />}
                                          </div>
                                          <div>
                                            <span className="text-sm font-medium">{addon.name}</span>
                                            {addon.duration !== "0 min" && (
                                              <span className="text-xs text-muted-foreground ml-2">+{addon.duration}</span>
                                            )}
                                          </div>
                                        </div>
                                        <span className="text-sm text-primary font-semibold">+${addon.price}</span>
                                      </button>
                                    );
                                  })}

                                  {/* Selected total */}
                                  {selectedAddonIds.size > 0 && (
                                    <div className="mt-2 p-3 bg-primary/5 border border-primary/20 flex justify-between items-center">
                                      <span className="text-xs text-muted-foreground">
                                        {selectedAddonIds.size} add-on(s) selected
                                      </span>
                                      <span className="text-sm font-semibold text-primary">
                                        +${categoryAddons.filter((a) => selectedAddonIds.has(a.id)).reduce((s, a) => s + a.price, 0)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-muted-foreground text-xs py-4 text-center">No add-ons available</p>
                              )}

                              {/* Book button */}
                              <Button
                                onClick={() => handleBook(category.title, sub.name, category.id)}
                                className="w-full mt-4 rounded-none py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90"
                              >
                                Book Now
                              </Button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(40_80%_42%/0.06),transparent_70%)]" />
        <div className="container mx-auto px-6 text-center relative">
          <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-4">Ready?</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold italic mb-4">
            Book Your Appointment
          </h2>
          <p className="text-muted-foreground mb-8 font-light max-w-md mx-auto">
            Choose your service and secure your spot today.
          </p>
          <Button
            onClick={() => navigate("/book")}
            className="rounded-none px-12 py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90"
          >
            Book Now
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
